import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { Product, Category } from '../types';
import ImageUpload from '../components/ImageUpload';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { buildCategoryTree, resolveCategoryId, findParentCategoryId } from '../utils/categoryHelpers';

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [categoryTree, setCategoryTree] = useState<Category[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stockFilter, setStockFilter] = useState<'all' | 'inStock' | 'outOfStock'>('all');
  const [productsPerPage, setProductsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [mainCategoryId, setMainCategoryId] = useState<string>('');
  const [subCategoryId, setSubCategoryId] = useState<string>('');
  const [formData, setFormData] = useState({
    photo: '',
    nama_produk: '',
    deskripsi_produk: '',
    stock: '0',
  });
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, stockFilter]);

  const filterProducts = () => {
    let filtered = products.filter(product => {
      const matchesSearch = product.nama_produk.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.deskripsi_produk.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStock = stockFilter === 'all' ? true :
                          stockFilter === 'inStock' ? product.stock > 0 :
                          product.stock === 0;
      
      return matchesSearch && matchesStock;
    });
    
    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, startIndex + productsPerPage);

  const fetchCategories = async () => {
    const { data } = await supabase.from('habib_categories').select('*').order('name', { ascending: true });
    if (data) {
      setAllCategories(data);
      setCategoryTree(buildCategoryTree(data));
    }
  };

  const fetchProducts = async () => {
    const { data } = await supabase
      .from('habib_products')
      .select('*, category:habib_categories(*)')
      .order('created_at', { ascending: false });
    if (data) {
      setProducts(data);
      setFilteredProducts(data);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const resolvedCategoryId = resolveCategoryId(
      mainCategoryId ? parseInt(mainCategoryId) : null,
      subCategoryId ? parseInt(subCategoryId) : null
    );

    const submitData = {
      photo: formData.photo,
      nama_produk: formData.nama_produk,
      deskripsi_produk: formData.deskripsi_produk,
      stock: parseInt(formData.stock) || 0,
      category_id: resolvedCategoryId
    };
    
    if (editingProduct) {
      await supabase
        .from('habib_products')
        .update(submitData)
        .eq('id', editingProduct.id);
    } else {
      await supabase.from('habib_products').insert([submitData]);
    }
    
    fetchProducts();
    resetForm();
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);

    // Derive main/sub category from product's category_id
    let mainId = '';
    let subId = '';
    if (product.category_id) {
      const parentId = findParentCategoryId(product.category_id, allCategories);
      if (parentId) {
        // Product is assigned to a sub-category
        mainId = parentId.toString();
        subId = product.category_id.toString();
      } else {
        // Product is assigned to a main category
        mainId = product.category_id.toString();
      }
    }
    setMainCategoryId(mainId);
    setSubCategoryId(subId);

    setFormData({
      photo: product.photo,
      nama_produk: product.nama_produk,
      deskripsi_produk: product.deskripsi_produk,
      stock: product.stock.toString(),
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    await supabase.from('habib_products').delete().eq('id', id);
    fetchProducts();
  };

  const resetForm = () => {
    setFormData({ photo: '', nama_produk: '', deskripsi_produk: '', stock: '0' });
    setMainCategoryId('');
    setSubCategoryId('');
    setEditingProduct(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold" style={{color: '#05472A'}}>{t('guestServingProductManagement')}</h2>
          <p className="text-sm sm:text-base" style={{color: '#05472A99'}}>{t('manageGuestServingCatalog')}</p>
        </div>
        <button 
          onClick={() => setShowForm(true)} 
          className="text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium transform hover:scale-105 active:scale-95 flex items-center gap-2"
          style={{backgroundColor: '#05472A'}}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t('addProduct')}
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-2xl shadow-soft border p-4 sm:p-6 space-y-4" style={{borderColor: '#05472A20'}}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t('searchProducts')}
              className="w-full px-4 py-3 pl-12 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
              style={{borderColor: '#05472A20'}}
            />
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex gap-2">
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value as any)}
              className="px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
              style={{borderColor: '#05472A20'}}
            >
              <option value="all">{t('allStock')}</option>
              <option value="inStock">{t('inStock')}</option>
              <option value="outOfStock">{t('outOfStock')}</option>
            </select>
            <select
              value={productsPerPage}
              onChange={(e) => {setProductsPerPage(Number(e.target.value)); setCurrentPage(1);}}
              className="px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
              style={{borderColor: '#05472A20'}}
            >
              <option value={12}>12 {t('perPage')}</option>
              <option value={33}>33 {t('perPage')}</option>
              <option value={40}>40 {t('perPage')}</option>
            </select>
          </div>
        </div>
        <div className="text-sm" style={{color: '#05472A99'}}>
          {t('showingOf')} {startIndex + 1}-{Math.min(startIndex + productsPerPage, filteredProducts.length)} {t('of')} {filteredProducts.length} {t('productsText')}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-strong max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{borderColor: '#05472A20'}}>
            <div className="sticky top-0 bg-white border-b p-6 rounded-t-2xl" style={{borderColor: '#05472A20'}}>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold" style={{color: '#05472A'}}>
                  {editingProduct ? t('editProduct') : t('addNewProduct')}
                </h3>
                <button 
                  onClick={resetForm}
                  className="p-2 rounded-xl hover:bg-green-50 transition-colors"
                  style={{color: '#05472A'}}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>{t('productImage')}</label>
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-xs text-blue-700">
                    <p className="font-semibold mb-1">{t('imageRequirements')}:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Max size: <strong>5MB</strong></li>
                      <li>Recommended: <strong>800x800px</strong> (1:1 ratio)</li>
                      <li>Format: JPG, PNG, WebP</li>
                    </ul>
                  </div>
                </div>
              </div>
              <ImageUpload
                onImageUploaded={(url) => setFormData({...formData, photo: url})}
                currentImage={formData.photo}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>{t('productName')}</label>
              <input
                type="text"
                placeholder={t('enterProductName')}
                value={formData.nama_produk}
                onChange={(e) => setFormData({...formData, nama_produk: e.target.value})}
                className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
                style={{borderColor: '#05472A20'}}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>{t('description')}</label>
              <textarea
                placeholder={t('enterProductDescription')}
                value={formData.deskripsi_produk}
                onChange={(e) => setFormData({...formData, deskripsi_produk: e.target.value})}
                className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300 resize-none"
                style={{borderColor: '#05472A20'}}
                rows={4}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>{t('category')}</label>
              <select
                value={mainCategoryId}
                onChange={(e) => { setMainCategoryId(e.target.value); setSubCategoryId(''); }}
                className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
                style={{borderColor: '#05472A20'}}
              >
                <option value="">{t('noCategory')}</option>
                {categoryTree.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            {mainCategoryId && (() => {
              const selectedMain = categoryTree.find(c => c.id === parseInt(mainCategoryId));
              const subs = selectedMain?.subcategories || [];
              if (subs.length === 0) return null;
              return (
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>Sub-Category</label>
                  <select
                    value={subCategoryId}
                    onChange={(e) => setSubCategoryId(e.target.value)}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
                    style={{borderColor: '#05472A20'}}
                  >
                    <option value="">None (Main Category Only)</option>
                    {subs.map((sub) => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              );
            })()}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>{t('stockQuantity')}</label>
              <input
                type="number"
                placeholder="0"
                value={formData.stock}
                onChange={(e) => setFormData({...formData, stock: e.target.value})}
                className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
                style={{borderColor: '#05472A20'}}
                min="0"
                required
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
              <button 
                type="submit" 
                className="flex-1 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium transform hover:scale-105 active:scale-95"
                style={{backgroundColor: '#05472A'}}
              >
                {editingProduct ? t('updateProduct') : t('createProduct')}
              </button>
              <button 
                type="button" 
                onClick={resetForm} 
                className="flex-1 bg-white hover:bg-green-50 border px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium"
                style={{color: '#05472A', borderColor: '#05472A40'}}
              >
                {t('cancel')}
              </button>
            </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {currentProducts.map((product) => (
          <div key={product.id} className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 overflow-hidden group hover:scale-105 border" style={{borderColor: '#05472A20'}}>
            <div className="relative overflow-hidden">
              <img 
                src={product.photo} 
                alt={product.nama_produk}
                className="w-full h-40 sm:h-48 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute top-3 right-3">
                <span className={`px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-sm sm:text-base font-bold transition-all duration-300 ${
                  product.stock > 0 
                    ? 'bg-green-100 border' 
                    : 'bg-red-100 text-red-700 border border-red-200'
                }`} style={product.stock > 0 ? {color: '#05472A', borderColor: '#05472A40'} : {}}>
                  {product.stock > 0 ? `${product.stock} ${t('available')}` : t('outOfStock')}
                </span>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div className="p-4 sm:p-5">
              <h3 className="font-bold mb-2 text-sm sm:text-base line-clamp-2" style={{color: '#05472A'}}>{product.nama_produk}</h3>
              {product.category && (
                <span className="inline-block px-2 py-1 rounded-lg text-xs font-semibold mb-2" style={{backgroundColor: '#05472A20', color: '#05472A'}}>
                  {product.category.name}
                </span>
              )}
              <p className="text-xs sm:text-sm mb-4 line-clamp-2" style={{color: '#05472A99'}}>{product.deskripsi_produk}</p>
              <div className="flex gap-2">
                <button 
                  onClick={() => handleEdit(product)}
                  className="flex-1 bg-white hover:bg-green-50 border px-3 py-2 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm flex items-center justify-center gap-2"
                  style={{color: '#05472A', borderColor: '#05472A40'}}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('edit')}
                </button>
                <button 
                  onClick={() => handleDelete(product.id)}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm flex items-center justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-2xl shadow-soft border p-4" style={{borderColor: '#05472A20'}}>
          <div className="flex justify-between items-center">
            <p className="text-sm" style={{color: '#05472A99'}}>
              {t('pageOf')} {currentPage} {t('of')} {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50"
                style={{borderColor: '#05472A40', color: '#05472A'}}
              >
                ←
              </button>
              <span className="px-3 py-1 font-medium" style={{color: '#05472A'}}>
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50"
                style={{borderColor: '#05472A40', color: '#05472A'}}
              >
                →
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredProducts.length === 0 && (
        <div className="bg-white rounded-2xl shadow-soft p-8 text-center border" style={{borderColor: '#05472A20'}}>
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#05472A10'}}>
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{color: '#05472A'}}>{searchTerm || stockFilter !== 'all' ? t('noProductsFound') : t('noProductsYet')}</h3>
          <p style={{color: '#05472A99'}}>{searchTerm || stockFilter !== 'all' ? t('tryAdjustingSearch') : t('addFirstProduct')}</p>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
